<template>
	<template v-if="maintenanceMode">
		<div>Sorry, we are currently down for maintenance. Please check back soon.</div>
	</template>
	<template v-else>
		<div
			v-if="!isOnline"
			class="fixed bottom-0 text-center w-full text-white bg-evergreen-800 z-50 p-2"
		>
			{{ $t('You are offline...') }}
		</div>
		<div>
			<notifications />
			<router-view />
		</div>
	</template>
</template>
<script lang="ts">
import { defineComponent, computed } from 'vue'
import { DEFAULT_APP_TITLE } from '@/modules/common/config'
import Navbar from '@/components/common/navbar/Navbar.vue'
import { isAppleDevice } from '@/modules/common/navigatorUtils'
import { useOnline } from '@vueuse/core'
import { RemoteLog } from '@/modules/common/utils/loggingUtils'
import { ClientSteps } from '@/modules/onboarding/composables/useOnboarding'
import router from '@/router/router'

export default defineComponent({
	components: { Navbar },
	data() {
		return {
			maintenanceMode: import.meta.env.VITE_MAINTENANCE_MODE === 'true',
		}
	},
	computed: {
		auth0IsAuthenticated() {
			return this.$auth0.isAuthenticated.value
		},
		isClientOnboarding() {
			// TODO: We probably want different logic to determine if a user is redirected to home after onboarding
			return this.$router.currentRoute.value.fullPath.toString().includes('/onboarding/client/')
		},
	},
	setup() {
		const isOnline = useOnline()

		return {
			isOnline,
		}
	},
	methods: {
		checkNonAppleDevice() {
			if (isAppleDevice()) {
				return
			}
			document.documentElement.classList.add('non-apple-device')
		},
		setTitle() {
			let routeName = this.$route.name ? String(this.$route.name) : ''
			if (routeName) {
				document.title = `${routeName} - ${DEFAULT_APP_TITLE}`
			} else {
				document.title = DEFAULT_APP_TITLE
			}
		},
		async tryTokenLogin() {
			const { loginToken } = this.$route.query
			if (!loginToken) {
				return
			}

			const query = { ...this.$route.query }
			delete query.loginToken

			this.$router.replace({ query })
			await this.$store.dispatch('auth/loginWithToken', {
				token: loginToken,
				redirect: true,
				isLoginAs: true,
			})
			await this.$router.push('/home')
		},
		async tryAuth0LoginToken(token: string) {
			await this.$store.dispatch('auth/loginWithAuth0Token', {
				token: token,
				redirect: true,
			})
		},
		loadScripts(url: string, async: boolean, defer: boolean, onResolve = () => {}): void {
			const script = document.createElement('script')
			script.src = url
			script.async = async
			script.defer = defer
			script.onload = () => onResolve()

			document.body.appendChild(script)
		},
		async getAuth0Token() {
			return await this.$auth0.getAccessTokenSilently()
		},
	},

	watch: {
		$route: {
			async handler() {
				this.setTitle()

				await this.tryTokenLogin()

				if (this.isClientOnboarding) {
					return
				}

				if (this.$router.currentRoute.value.fullPath.toString() === '/') {
					return
				}

				// Visitors arrive from the marketing site, often framed, where a stale session's 401 would bounce them to Auth0.
				if (this.$route.meta.public === true) {
					return
				}

				if (this.$isLoggedIn) {
					this.$store.dispatch('auth/getAnnouncements')
					this.$store.dispatch('secureMessages/getUnreadCount')
				}
			},
			immediate: true,
		},
		auth0IsAuthenticated: {
			async handler(newVal) {
				// TODO: We probably want different logic to determine if a user is redirected to home after onboarding
				if (this.isClientOnboarding) {
					return
				}
				if (newVal && !this.$isLoggedIn) {
					RemoteLog.info({ message: 'Signing in from Auth0 session' })
					const token = await this.getAuth0Token()
					await this.tryAuth0LoginToken(token)
				}
			},
			immediate: true,
		},
		// This functionality accounts for logging out the user
		$isLoggedIn: {
			async handler(newVal, oldVal) {
				if (newVal || !oldVal) {
					return
				}

				// Auth0's logout redirect would carry a guest off the public page they came for.
				if (this.$route.meta.public === true) {
					this.$auth0.logout({ openUrl: false })
					return
				}

				this.$auth0.logout()
			},
			immediate: true,
		},
	},
	async mounted() {
		this.checkNonAppleDevice()
	},
	created() {
		// The analytics/RUM scripts below record window.location, so defer them until the router has
		// settled the initial URL (it rewrites it once on boot) and we've stripped any credential
		// token — password-reset, public results, MDI verification — from it. vue-router keeps its
		// own parsed copy, so the page still reads `$route.query.token`.
		this.$router.isReady().then(() => {
			try {
				const url = new URL(window.location.href)
				if (url.searchParams.has('token')) {
					url.searchParams.delete('token')
					window.history.replaceState(
						window.history.state,
						'',
						url.pathname + url.search + url.hash,
					)
				}
			} catch {
				// Never block boot on URL cleanup.
			}

			window.SegmentKey = import.meta.env.VITE_SEGMENT_KEY
			this.loadScripts('https://www.googleoptimize.com/optimize.js?id=OPT-5RTZWXC', true, false)
			this.loadScripts(
				'https://www.googletagmanager.com/gtag/js?id=G-RP6EDKZ598',
				true,
				true,
				() => {
					if (!/HeadlessChrome/.test(window.navigator.userAgent) && !window.navigator.webdriver) {
						this.loadScripts('/gta.js', true, true)
					}
				},
			)
			this.loadScripts('/speedcurve-rum.js', false, false)
			this.loadScripts('https://cdn.speedcurve.com/js/lux.js?id=4474276114', true, true)
		})
	},
	unmounted() {},
})
</script>
